import {
  AdvancedFilterDataSchema,
  FilterGroupSchema,
} from "@redotech/redo-views-common/advanced-filters/advanced-filter-data";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";

export const fulfillmentGroupSearchFields = [
  "item-sku",
  "item-name",
  "product-variant",
  "order-date",
  "order-age",
  "order",
  "customString1",
  "customString2",
  "customString3",
  "customDate1",
  "customDate2",
  "customDate3",
  "customNumber1",
  "customNumber2",
  "customNumber3",
  "customAttributeString1",
  "customAttributeString2",
  "customAttributeString3",
  "customAttributeDate1",
  "customAttributeDate2",
  "customAttributeDate3",
  "customAttributeNumber1",
  "customAttributeNumber2",
  "customAttributeNumber3",
  "shippingSelection",
  "shipped-date",
  "customer",
  "destination-country",
  "quantity",
  "weight",
  "requested-service",
  "total",
  "slaFulfillmentDate",
  "fulfillment-location",
  "is-pick-list-printed",
  "is-packing-slip-printed",
  "is-label-printed",
  "total-sku-count",
  "delivery-method",
  "insured",
  "stores",
  "scan-verified",
  "address-verification",
  "batch-id",
  "bin",
  "shipment-created-at",
  "vendors",
  "delivery-status",
  "estimated-delivery-date",
  "originStore",
  "labelPurchasedDate",
  "fraudRisk",
  "state",
  "packing-slip-printed-at",
  "order-name",
  "customer-po",
  "customer-id",
  "rate",
  "productTags",
  "missingBins",
  "customerOrderCount",
  "parcel-selection",
  "used-weight-learning",
  "used-parcel-learning",
  "tag",
  "missingPickItems",
  "internal-notes",
  "numericId",
  "updated-at",
] as const;

export type FulfillmentGroupSearchField =
  (typeof fulfillmentGroupSearchFields)[number];

export const fulfillmentGroupFilterNames = [
  "pickStatus",
  "pickedAt",
  "pickStartedOn",
  "assignedPicker",
  "deliveryMethod",
  "batchSuggestionKey",
  "labelDate",
  "fulfilled-date",
  "createdAt",
  "updatedAt",
  "itemSkus",
  "itemBarcodes",
  "itemRedoProductIds",
  "slaDeliveryPromise",
  "itemQuantity",
  "totalPrice",
  "status",
  "itemTitle",
  "productTitle",
  "productType",
  "source",
  "assignees",
  "countries",
  "state",
  "requestedService",
  "carrier",
  "originAddressName",
  "fulfillmentLocation",
  "order",
  "tagName",
  "shopifyOrderTags",
  "shopifyProductTags",
  "productVariant",
  "shippingInsurance",
  "labelPrinted",
  "orderNote",
  "packingSlipPrinted",
  "pickListPrinted",
  "parcelWeight",
  "lineItemQuantity",
  "selectedCarrier",
  "stores",
  "salesChannel",
  "scanVerified",
  "batchId",
  "orderCreatedAt",
  "vendors",
  "shippedDate",
  "labelPurchasedDate",
  "fulfilledBy",
  "commercialInvoiceElectronic",
  "addressVerified",
  "bin",
  "deliveryStatus",
  "estimatedDeliveryDate",
  "originStore",
  "fraudRisk",
  "shipmentVoided",
  "shipmentRateMode",
  "boughtRateCarrier",
  "boughtRateProvider",
  "shipmentScanFormId",
  "shipmentScanFormBatchState",
  "shipmentScanFormStatus",
  "canadaPostManifestRequired",
  "packingSlipPrintedAt",
  "fulfillmentOrderCount",
  "customerOrderCount",
  "uspsParcelConstraints",
  "customAttributeDate1",
  "customAttributeDate2",
  "customAttributeDate3",
  "customAttributeNumber1",
  "customAttributeNumber2",
  "customAttributeNumber3",
  "customAttributeString1",
  "customAttributeString2",
  "customAttributeString3",
  "customString1",
  "customString2",
  "customString3",
  "customDate1",
  "customDate2",
  "customDate3",
  "customNumber1",
  "customNumber2",
  "customNumber3",
  "customer",
  "customerTags",
  "productTags",
  "missingBins",
  "locationId",
  "pickSessionId",
  "pickSessionIdValue",
  "selectedParcelsName",
  "usedWeightLearning",
  "usedParcelLearning",
  "voidedBatchId",
  "numericId",
  "expandedItemQuantity",
  "assignedFulfillmentService",
  "inventorySource",
  "pickability",
  "warehouseMissingBins",
  "pickableBinCount",
  "productBinAssignmentDate",
  "noNonExpiredInventoryInWarehouse",
  "noInventoryInWarehouse",
  "variantCustomString1",
  "variantCustomString2",
  "variantCustomString3",
  "variantCustomDate1",
  "variantCustomDate2",
  "variantCustomDate3",
  "variantCustomNumber1",
  "variantCustomNumber2",
  "variantCustomNumber3",
  "marketplaceNotificationStatus",
  "missingPickItemSkus",
  "fulfillmentGroupId",
  "labelPurchaseFailedAt",
  "hasSelectedShippingRate",
  "startZoneId",
] as const;

const wildcardFields = [
  "itemSkus",
  "itemBarcodes",
  "itemTitle",
  "missingPickItemSkus",
  "orderNote",
  "productVariant",
  "shopifyOrderTags",
] as const satisfies FulfillmentGroupFilterName[];
export type FulfillmentGroupWildcardFilter = (typeof wildcardFields)[number];

export const fulfillmentGroupKeywordFilters = [
  "batchId",
  "bin",
  "countries",
  "customAttributeString1",
  "customAttributeString2",
  "customAttributeString3",
  "customString1",
  "customString2",
  "customString3",
  "deliveryMethod",
  "fraudRisk",
  "fulfilledBy",
  "itemSkus",
  "itemBarcodes",
  "itemRedoProductIds",
  "itemTitle",
  "assignees",
  "assignedPicker",
  "order",
  "orderNote",
  "originAddressName",
  "originStore",
  "productVariant",
  "requestedService",
  "salesChannel",
  "selectedCarrier",
  "shopifyOrderTags",
  "source",
  "state",
  "status",
  "pickStatus",
  "tagName",
  "vendors",
  "boughtRateCarrier",
  "boughtRateProvider",
  "fulfillmentLocation",
  "stores",
  "deliveryStatus",
  "productTags",
  "batchSuggestionKey",
  "customer",
  "customerTags",
  "locationId",
  "pickSessionIdValue",
  "productTitle",
  "productType",
  "selectedParcelsName",
  "voidedBatchId",
  "assignedFulfillmentService",
  "inventorySource",
  "variantCustomString1",
  "variantCustomString2",
  "variantCustomString3",
  "marketplaceNotificationStatus",
  "missingPickItemSkus",
  "fulfillmentGroupId",
  "startZoneId",
] as const satisfies readonly FulfillmentGroupFilterName[];
export type FulfillmentGroupKeywordFilter =
  (typeof fulfillmentGroupKeywordFilters)[number];

const fulfillmentGroupListMatchValuesFields = [
  "shopifyOrderTags",
  "requestedService",
  "itemSkus",
  "itemBarcodes",
  "missingPickItemSkus",
  "productTitle",
  "productType",
  "stores",
  "shopifyProductTags",
  "originStore",
  "customString1",
  "customString2",
  "customString3",
  "customAttributeString1",
  "customAttributeString2",
  "customAttributeString3",
  "customer",
  "customerTags",
  "vendors",
  "productTags",
  "productVariant",
  "variantCustomString1",
  "variantCustomString2",
  "variantCustomString3",
] as const satisfies FulfillmentGroupFilterName[];
export type FulfillmentGroupListMatchValuesFilterName =
  (typeof fulfillmentGroupListMatchValuesFields)[number];

export type FulfillmentGroupFilterName =
  (typeof fulfillmentGroupFilterNames)[number];

export const AdvancedFilterInputSchema = z.object({
  // put shared search, sort, filters here
  advancedFiltersData: z.array(AdvancedFilterDataSchema),
  // Optional nested filter group for advanced AND/OR logic
  // If present, this takes precedence over advancedFiltersData
  filterGroup: FilterGroupSchema.optional(),
  sort: z
    .object({
      key: z.string(),
      direction: createEnum(["asc", "desc"], {
        enumName: "FulfillmentGroupSortDirection",
      }),
    })
    .nullish(),
  search: z.string().nullish(),
  merchantTimezone: z.string().nullish(),
});
export type AdvancedFilterInput = z.infer<typeof AdvancedFilterInputSchema>;
